import React, {useCallback, useMemo} from 'react';
import type {SxProps, Theme} from '@mui/material';
import {
  AppBar,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  Toolbar,
  Typography,
} from '@mui/material';
import {useLocation, useNavigate} from 'react-router';
import {Configuration, isFeatureEnabled} from 'configuration';
import {useLocalization} from 'localization';
import {px2rem, useThemeColors} from 'theme';
import {useUser} from 'services/auth';
import {fontAdjusted, fontResetted, localeUpdated} from 'services/settings';
import {useShortcut} from 'services/shortcuts';
import {useAppDispatch} from 'store/hooks';
import {Avatar} from 'components/avatar';
import {MenuButton} from 'components/button';
import {Link} from 'components/button/Link';
import {LessIcon, MoreIcon, RadioOnIcon} from 'components/icons';
import {FlexBox} from 'components/layout';
import {Spacer} from 'components/spacer';
import {Logo} from 'components/svg';
import {SystemHealthWidget} from 'widgets/systemHealth/SystemHealthWidget';
import {TopBarAnon} from './TopbarAnon';
import {EnvironmentDisplay} from './components/EnvironmentDisplay';
import {IsbnSearch} from './components/IsbnSearch';
import {ShortcutsTooltip} from './components/ShortcutsTooltip';

type Props = {
  role: 'user' | 'admin';
};

const isCurrentPath = (navPath: string, currentPathName: string) => {
  const currentPath =
    currentPathName[0] === '/' ? currentPathName.substring(1) : currentPathName;
  const firstElement = currentPath.split('/')[0];
  return navPath === firstElement;
};

const appbarSx: SxProps<Theme> = {
  backgroundClip: 'padding-box',
  zIndex: theme => theme.zIndex.drawer + 1,
};

const originTopRight = {
  vertical: 'top',
  horizontal: 'right',
} as const;

const toolbarSx: SxProps<Theme> = {
  paddingX: 3,
  justifyContent: 'space-between',
  background: ({palette}) => palette.primary.main,
};
const menuButtonSx: SxProps<Theme> = {
  p: 0.5,
  color: ({palette}) => palette.primary.main,
};
const menuSx: SxProps<Theme> = {mt: px2rem(45)};

export const TopBar: React.FC<Props> = () => {
  const {t, tLoose, getPreferredLocale} = useLocalization();
  const themeColors = useThemeColors();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const user = useUser();
  useShortcut('goToOverview', () => navigate('/overview'));

  const pages = useMemo(() => {
    const p = {
      user: [
        {title: t('appbar.overview'), path: 'overview'},
        {title: t('appbar.advancedSearch'), path: 'search'},
        {title: t('appbar.agents'), path: 'agent'},
        {title: t('appbar.prizes'), path: 'prize'},
      ].filter(page => page !== null) as {title: string; path: string}[],
      settings: [
        {
          title: `${t('appbar.menu.language')}: ${tLoose(
            'general.language.' + getPreferredLocale(),
          )}`,
          action: 'toggleLanguage',
        },
        {title: t('appbar.menu.shortcuts'), action: 'gotoShortcuts'},
        {title: t('appbar.menu.logout'), action: 'logout'},
      ],
    };

    p.user.push({title: t('appbar.bulkUpdate'), path: 'bulkUpdate'});

    if (isFeatureEnabled('mergeSuggestions')) {
      p.user.push({title: t('appbar.mergeOverview'), path: 'mergeOverview'});
    }
    if (isFeatureEnabled('receiveRoute')) {
      p.user.push({title: t('appbar.receive'), path: 'receive'});
    }

    p.user.push({title: t('appbar.updateLog'), path: 'updateLog'});

    if (Configuration.env === 'local' || Configuration.env === 'dev') {
      p.user.push({title: t('appbar.demo'), path: 'demo'});
      p.user.push({title: t('appbar.demoSearch'), path: 'demoSearch'});
    }

    if (Configuration.env === 'local') {
      p.user.push({title: t('appbar.test'), path: 'test'});
    }
    return p;
  }, [t, tLoose, getPreferredLocale]);

  const [anchorElUser, setAnchorElUser] = React.useState<
    (EventTarget & HTMLButtonElement) | null
  >(null);
  const handleOpenUserMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorElUser(event.currentTarget);
  };
  const handleCloseUserMenu = () => {
    setAnchorElUser(null);
  };

  const handleUserMenuItem = useCallback(
    (title: string) => {
      switch (title) {
        case 'toggleLanguage': {
          dispatch(localeUpdated(getPreferredLocale() === 'en' ? 'nb' : 'en'));
          break;
        }
        case 'gotoShortcuts': {
          navigate('/shortcuts');
          break;
        }
        case 'logout': {
          navigate('/logout');
          break;
        }
      }

      handleCloseUserMenu();
    },
    [dispatch, getPreferredLocale, navigate],
  );

  const handleSmallerFont = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      dispatch(fontAdjusted(-5));
      event.preventDefault();
      event.stopPropagation();
    },
    [dispatch],
  );

  const handleResetFont = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      dispatch(fontResetted());
      event.preventDefault();
      event.stopPropagation();
    },
    [dispatch],
  );

  const handleLargerFont = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      dispatch(fontAdjusted(5));
      event.preventDefault();
      event.stopPropagation();
    },
    [dispatch],
  );

  if (!user) {
    return <TopBarAnon />;
  }

  return (
    <AppBar data-cy="main-app-bar" sx={appbarSx}>
      <Toolbar disableGutters sx={toolbarSx}>
        <Stack
          direction="row"
          spacing={2}
          sx={{
            alignItems: 'center',
          }}>
          <FlexBox position={'relative'}>
            <EnvironmentDisplay environment={Configuration.env} />
            <Link to={'/'} title={'Ebba'}>
              <Logo color={themeColors.background.topBar.light} />
            </Link>
          </FlexBox>
          {pages.user.map(page => (
            <MenuButton
              key={page.path}
              path={page.path}
              title={page.title}
              selected={isCurrentPath(page.path, location.pathname)}
            />
          ))}
        </Stack>

        <Stack
          direction="row"
          spacing={2}
          sx={{
            alignItems: 'center',
          }}>
          <Spacer size={4} />
          <ShortcutsTooltip />
          <SystemHealthWidget />
          <IsbnSearch />
          <IconButton onClick={handleOpenUserMenu} sx={menuButtonSx}>
            <Avatar src={user.picture} />
          </IconButton>
          <Menu
            sx={menuSx}
            id="menu-appbar"
            anchorEl={anchorElUser}
            anchorOrigin={originTopRight}
            keepMounted
            transformOrigin={originTopRight}
            open={Boolean(anchorElUser)}
            onClose={handleCloseUserMenu}>
            <MenuItem>
              <FlexBox>
                <Typography
                  sx={{
                    textAlign: 'center',
                  }}>
                  {t('appbar.menu.fontSize')}
                </Typography>
                <FlexBox horizontal px={1}>
                  <IconButton onClick={handleSmallerFont}>
                    <LessIcon />
                  </IconButton>
                  <IconButton onClick={handleResetFont}>
                    <RadioOnIcon />
                  </IconButton>
                  <IconButton onClick={handleLargerFont}>
                    <MoreIcon />
                  </IconButton>
                </FlexBox>
              </FlexBox>
            </MenuItem>
            {pages.settings.map(setting => (
              <MenuItem
                key={setting.action}
                onClick={() => handleUserMenuItem(setting.action)}>
                <Typography
                  sx={{
                    textAlign: 'center',
                  }}>
                  {setting.title}
                </Typography>
              </MenuItem>
            ))}
          </Menu>
        </Stack>
      </Toolbar>
    </AppBar>
  );
};
